import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getMessage } from '@a0/support-center-common/lib/getMessage';
import { getHostEnvironmentWithCloudName } from '@a0/support-center-common/lib/tenants';
import { tenantRoles } from '@a0/support-center-common/constants/tenants';
import base64imgs from '@a0/support-center-common/assets/platformIcons';

import Tooltip from '../Tooltip';
import Card from '../Card';
import Flag from '../Flag';
import Label from '../Label';
import SettingsList from '../SettingsList';
import TenantEnvironmentForm from '../TenantEnvironmentForm';
import HotjarMasked from '../HotjarMasked';

import getTenantTaggingSuggestion from '../../utils/getTenantTaggingSuggestion';
import noop from '../../utils/noop';
import { formatDate } from '../../utils/dateUtility';

import TooltipSuggestion from './tooltipSuggestion';
import TaggingSuggestion from './taggingSuggestion';



import './styles.css';

const DEFAULT_DATE_FORMAT = 'LLL';

class TenantCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      flipped: false,
      showTenantEnvironment: false,
      isSuggestionTooltipHidden: this.isSuggestionHidden(
        !props.withTooltipSuggestion,
        'suggestion:tooltip'
      ),
      isSuggestionTaggingHidden: this.isSuggestionHidden(
        !props.withTaggingSuggestion,
        'suggestion:tagging'
      )
    };

    this.renderHeader = this.renderHeader.bind(this);
    this.getManageUrl = this.getManageUrl.bind(this);
    this.flippedCallback = this.flippedCallback.bind(this);
    this.toggledCallback = this.toggledCallback.bind(this);

    this.showTenantEnvironment = this.showTenantEnvironment.bind(this);
    this.hideTenantEnvironment = this.hideTenantEnvironment.bind(this);

    this.hideSuggestion = this.hideSuggestion.bind(this);
    this.renderToggleIcon = this.renderToggleIcon.bind(this);

    this.getSuggestedTaggingMessage = this.getSuggestedTaggingMessage.bind(
      this
    );
  }

  componentDidMount() {
    const { withTooltipSuggestion } = this.props;

    if (withTooltipSuggestion) {
      setTimeout(() => {
        this.hideSuggestion('suggestion:tooltip')();
        this.setState({ isSuggestionTooltipHidden: true });
      }, 10000);
    }
  }

  getManageUrl(baseManageUrl) {
    const { name, region } = this.props;
    return `${baseManageUrl}/dashboard/${region}/${name}`;
  }

  getSettings() {
    const {
      toggleShowChecklistModal,
      runChecksSettingTooltip,
      assignEnvTagSettingTooltip,
      showInspectTenantPage,
      showDashboardAdmins,
      showSupportCenterUsers
    } = this.props;

    const settings = [
      {
        name: 'Run Production Check',
        action: toggleShowChecklistModal,
        tooltip: runChecksSettingTooltip
      },
      {
        name: 'Assign Environment Tag',
        action: this.showTenantEnvironment,
        tooltip: assignEnvTagSettingTooltip
      }
    ];

    if (showInspectTenantPage) {
      settings.push({
        name: 'Inspect',
        action: showInspectTenantPage
      });
    }

    if (showDashboardAdmins) {
      settings.push({
        name: 'Manage Dashboard Admins',
        action: showDashboardAdmins
      });
    }

    if (showSupportCenterUsers) {
      settings.push({
        name: 'Manage Support Center Users',
        action: showSupportCenterUsers
      });
    }

    return settings;
  }

  getSuggestedTaggingMessage() {
    const { name, environment, withTaggingSuggestion } = this.props;
    const { isSuggestionTaggingHidden } = this.state;

    const suggestedTagging = getTenantTaggingSuggestion(name);

    if (
      withTaggingSuggestion &&
      !isSuggestionTaggingHidden &&
      !environment &&
      suggestedTagging !== environment
    ) {
      return suggestedTagging != null
        ? `This looks like a <strong>${suggestedTagging.toUpperCase()}</strong> tenant. Please, <strong>Assign an Environment Tag</strong>.`
        : 'This tenant is currently not tagged. Please, <strong>Assign an Environment Tag</strong>.';
    }

    return '';
  }

  getSuggestionNameForLocalStorage(suggestionName) {
    const { name, subscriptionName } = this.props;

    return suggestionName === 'suggestion:tooltip' ? subscriptionName : name;
  }

  showTenantEnvironment(event) {
    if (event) {
      event.preventDefault();
    }

    // Hide Suggestion Tagging
    this.hideSuggestion('suggestion:tagging')();
    this.setState({ isSuggestionTaggingHidden: true });

    // Show TenantEnvironment
    this.setState({ showTenantEnvironment: true });
    this.props.Metrics.track('click:sc:tenant:environment');
  }

  hideTenantEnvironment(event) {
    if (event) {
      event.preventDefault();
    }

    this.setState({ showTenantEnvironment: false });
  }

  isAdmin() {
    const { role } = this.props;
    return role === tenantRoles.ADMIN_TENANT_ROLE || role === tenantRoles.INTERNAL_ROLE;
  }

  isInternal() {
    const { role } = this.props;
    return role === tenantRoles.INTERNAL_ROLE;
  }

  hideSuggestion(suggestionName) {
    const { Metrics } = this.props;
    const name = this.getSuggestionNameForLocalStorage(suggestionName);

    return () => {
      const hiddenSuggestionListStr = localStorage.getItem(suggestionName);
      const hiddenSuggestionList = hiddenSuggestionListStr
        ? JSON.parse(hiddenSuggestionListStr)
        : [];

      localStorage.setItem(
        suggestionName,
        JSON.stringify([...new Set(hiddenSuggestionList.concat(name))])
      );

      Metrics.track(`click:sc:tenant:${suggestionName}`);

      // Hide suggestion tooltip when the config icon is clicked
      if (suggestionName === 'suggestion:tooltip') {
        this.setState({ isSuggestionTooltipHidden: true });
      }
    };
  }

  isSuggestionHidden(isHidden, suggestionName) {
    const name = this.getSuggestionNameForLocalStorage(suggestionName);

    if (isHidden) {
      return true;
    }

    const hiddenSuggestionListStr = localStorage.getItem(suggestionName);

    const hiddenSuggestionList = hiddenSuggestionListStr
      ? JSON.parse(hiddenSuggestionListStr)
      : [];

    return (
      hiddenSuggestionList &&
      hiddenSuggestionList.length >= 1 &&
      hiddenSuggestionList.indexOf(name) >= 0
    );
  }

  flippedCallback() {
    this.props.Metrics.track('click:sc:tenant:config');
  }

  toggledCallback() {
    this.setState({ flipped: !this.state.flipped });
  }

  renderRegion() {
    const { region } = this.props;
    return <Flag region={region} />;
  }

  renderHeader() {
    const { name, cloud, cluster, host_environment } = this.props;
    return (
      <div>
        {this.renderEnvironment()}
        <h4 className="card__title">
          <HotjarMasked>{name}</HotjarMasked>
        </h4>
        {this.renderRegion()} {getHostEnvironmentWithCloudName(host_environment, cloud || cluster)}
      </div>
    );
  }

  renderEnvironment() {
    const { disabled, environment, updating } = this.props;

    if (environment) {
      const variant = (disabled ? 'status-disabled' : environment).toLowerCase();
      return (
        <Label variant={variant} updating={updating}>
          {environment}
        </Label>
      );
    }

    return (
      <span className="card--tenant__no-env">No environment assigned</span>
    );
  }

  renderPlan() {
    const { id, subscription, getSubscriptionTag } = this.props;

    if (subscription && !subscription.isCustomAgreement && getSubscriptionTag) {
      return (
        <div>
          <h5 className="card__section-header">
            <i className="card__icon icon-budicon-777" /> Plan
          </h5>
          {getSubscriptionTag(subscription, id)}
        </div>
      );
    }

    return null;
  }

  renderUsers() {
    const {
      active_users_last_30d: activeUsersLast30d,
      total_users: totalUsers
    } = this.props;

    return (
      <div>
        <h5 className="card__section-header">
          <i className="card__icon icon-budicon-300" /> Users
        </h5>
        <ul className="card__list">
          {/* <li>
            Active: {activeUsersLast30d || 0}{' '}
            {activeUsersLast30d ? <span>(last 30 days)</span> : ''}
          </li> */}
          <li>Total: {totalUsers || 0}</li>
        </ul>
      </div>
    );
  }

  renderClients() {
    const {
      total_clients: totalClients,
      total_connections: totalConnections
    } = this.props;
    return (
      <div>
        <h5 className="card__section-header">
          <i className="card__icon icon-budicon-375" /> {getMessage('CLIENTS')}
        </h5>
        <ul className="card__list">
          <li>Total Apps: {totalClients || 0}</li>
          <li>Total Connections: {totalConnections || 0}</li>
        </ul>
      </div>
    );
  }

  renderRules() {
    const { active_rules: activeRules, total_rules: totalRules } = this.props;

    return (
      <div>
        <h5 className="card__section-header">
          <i className="card__icon icon-budicon-173" /> Rules
        </h5>
        <ul className="card__list">
          <li>Active: {activeRules || 0}</li>
          <li>Total: {totalRules || 0}</li>
        </ul>
      </div>
    );
  }

  renderTenantCreationDate() {
    const { created_at: createdAt } = this.props;

    return (
      <div>
        <h5 className="card__section-header">
          <i className="card__icon icon-budicon-629" /> Creation Date
        </h5>
        <ul className="card__list">
          <li>{formatDate(createdAt, DEFAULT_DATE_FORMAT)}</li>
        </ul>
      </div>
    );
  }

  renderTam() {
    const { success_manager } = this.props;
    return (
      <div>
        <h5 className="card__section-header">
          <i className="card__icon icon-budicon-300" />TAM
        </h5>
        <ul className="card__list">
          <li>{success_manager ? success_manager : '--'}</li>
        </ul>
      </div>
    );
  }

  renderPlatform() {
    const { platform } = this.props;
    if (!platform) return;
    return (
      <div>
        <h5 className="card__section-header">
          <i className="card__icon">
            <img className="max-height-1rem" src={platform.indexOf('layer0') > -1 ? base64imgs.layer0 : base64imgs.cloud}/>
          </i> Platform
        </h5>
        <ul className="card__list">
          <li>{platform}</li>
        </ul>
      </div>
    );
  }

  renderFooter() {
    const { hideDashboard, trackClickManage, name, baseManageUrl } = this.props;
    const { flipped } = this.state;

    if (!hideDashboard && !flipped) {
      return (
        <div className="card__footer">
          {trackClickManage ? (
            <a
              href={this.getManageUrl(baseManageUrl)}
              onClick={trackClickManage(name, this.getManageUrl(baseManageUrl))}
            >
              Open Dashboard
            </a>
          ) : (
            <a href={this.getManageUrl(baseManageUrl)}>Open Dashboard</a>
          )}
        </div>
      );
    }

    return null;
  }

  renderToggleIcon(flipped) {
    const {
      withTooltipSuggestion,
      withTaggingSuggestion,
      name,
      environment,
      withSettings,
      disabled
    } = this.props;

    if (disabled) {
      return (
        <i
          style={{ lineHeight: '1', fontSize: '16px', marginTop: '1px' }}
          className="icon-budicon-330"
        />
      );
    }

    const { isSuggestionTooltipHidden, isSuggestionTaggingHidden } = this.state;
    const showTooltipSuggestion =
      withTooltipSuggestion && !isSuggestionTooltipHidden;
    const showTaggingSuggestion =
      withTaggingSuggestion && !isSuggestionTaggingHidden;

    return (
      <TooltipSuggestion
        show={showTooltipSuggestion}
        handleClick={this.hideSuggestion('suggestion:tooltip')}
      >
        <TaggingSuggestion
          show={showTaggingSuggestion}
          name={name}
          environment={environment}
        >
          <Tooltip
            show={!showTooltipSuggestion && !flipped}
            title={
              withSettings ? (
                'Click here to assign the Environment Tag or run the Production Check'
              ) : (
                'Click here to assign the Environment Tag'
              )
            }
            placement="bottom"
          >
            <i
              style={{ lineHeight: '1', fontSize: '16px', marginTop: '1px' }}
              className={`icon-budicon-${flipped ? 471 : 330}`}
            />
          </Tooltip>
        </TaggingSuggestion>
      </TooltipSuggestion>
    );
  }

  render() {
    const { environment, updating, reverseAction, withSettings, disabled } = this.props;

    const { showTenantEnvironment } = this.state;

    return (
      <Card
        updating={updating}
        header={this.renderHeader()}
        className={classNames({'card__disabled': disabled})}
        renderToggleIcon={this.renderToggleIcon}
        disabled={disabled}
        flippedCallback={this.flippedCallback}
        toggledCallback={this.toggledCallback}
      >
        <div>
          {this.renderPlan()}
          {this.renderUsers()}
          {this.isInternal() && this.renderPlatform()}
          {this.renderClients()}
          {this.renderRules()}
          {this.renderTenantCreationDate()}
          {this.renderFooter()}
        </div>
        {this.isAdmin() && (
          <div>
            {
              <SettingsList
                show={withSettings && !showTenantEnvironment}
                settings={this.getSettings()}
                warningMessage={this.getSuggestedTaggingMessage()}
              >
                <TenantEnvironmentForm
                  onSubmit={reverseAction}
                  environment={environment}
                  updating={updating}
                  handleClose={withSettings && this.hideTenantEnvironment}
                />
              </SettingsList>
            }
          </div>
        )}
      </Card>
    );
  }
}

TenantCard.propTypes = {
  active_rules: PropTypes.number,
  active_users_last_30d: PropTypes.number,
  baseManageUrl: PropTypes.string,
  cloud: PropTypes.string,
  cluster: PropTypes.string,
  created_at: PropTypes.string,
  disabled: PropTypes.bool,
  environment: PropTypes.string,
  getSubscriptionTag: PropTypes.func,
  hideDashboard: PropTypes.bool,
  host_environment: PropTypes.string,
  id: PropTypes.string,
  Metrics: PropTypes.object,
  name: PropTypes.string.isRequired,
  platform: PropTypes.string,
  region: PropTypes.string.isRequired,
  reverseAction: PropTypes.func,
  role: PropTypes.string.isRequired,
  showDashboardAdmins: PropTypes.func,
  showInspectTenantPage: PropTypes.func,
  showSupportCenterUsers: PropTypes.func,
  subscription: PropTypes.object,
  subscriptionName: PropTypes.string,
  success_manager: PropTypes.string,
  toggleShowChecklistModal: PropTypes.func,
  runChecksSettingTooltip: PropTypes.string,
  assignEnvTagSettingTooltip: PropTypes.string,
  total_clients: PropTypes.number,
  total_connections: PropTypes.number,
  total_rules: PropTypes.number,
  total_users: PropTypes.number,
  trackClickManage: PropTypes.func,
  updating: PropTypes.bool,
  withSettings: PropTypes.bool,
  withTaggingSuggestion: PropTypes.bool,
  withTooltipSuggestion: PropTypes.bool,
};

TenantCard.defaultProps = {
  Metrics: { track: noop },
  baseManageUrl: `https://manage.auth0.com`,
  disabled: false,
  withSettings: false
};

export default TenantCard;
